/**
 * Text handling for the masked date field.
 *
 * Kept free of React so the fiddly parts — masking as you type, working out
 * which segment the caret sits in, stepping a segment with the arrow keys — are
 * unit-testable on their own.
 *
 * The display format is fixed to `mm/dd/yyyy`, matching `formatDate` in
 * utils/formatters and the US-only business this serves. The stored format is
 * always ISO `yyyy-mm-dd`, which is what the native input used and what the API
 * expects, so nothing outside this component sees the display format.
 */

export const DATE_PLACEHOLDER = 'mm/dd/yyyy';

/** Character offsets of each segment in a full `mm/dd/yyyy` string. */
const SEGMENTS: ReadonlyArray<readonly [number, number]> = [
  [0, 2], // month
  [3, 5], // day
  [6, 10], // year
];

/** `2026-08-14` → `08/14/2026`. Anything unparseable becomes ''. */
export function isoToDisplay(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
  if (!match) return '';

  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
}

/**
 * `08/14/2026` → `2026-08-14`, or null when the text isn't a complete, real
 * date. Rejects impossible days (02/30) rather than letting the Date
 * constructor roll them over into the next month.
 */
export function displayToIso(display: string): string | null {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(display ?? '');
  if (!match) return null;

  const [, month, day, year] = match;
  const monthNumber = Number(month);
  const dayNumber = Number(day);
  const yearNumber = Number(year);

  if (monthNumber < 1 || monthNumber > 12) return null;
  if (dayNumber < 1) return null;
  if (yearNumber < 1) return null;

  // Day 0 of the next month is the last day of this one.
  const daysInMonth = new Date(Date.UTC(yearNumber, monthNumber, 0)).getUTCDate();
  if (dayNumber > daysInMonth) return null;

  return `${year}-${month}-${day}`;
}

/**
 * Normalises an explicitly-delimited date — a pasted `9/2/2026`, `09-02-2026`,
 * or ISO `2026-08-14` — into `mm/dd/yyyy`, zero-padding a single-digit month or
 * day. Returns null for anything that isn't a full three-part delimited date, so
 * applyDateMask's digit-stream path keeps handling typing and in-place edits.
 *
 * Only month and day are padded; the year is taken as-is (up to four digits) so a
 * half-typed `09/02/2` isn't turned into `09/02/0002`. A three-part string with a
 * single-digit month or day can only come from a paste — typing never produces
 * one, because the mask inserts a "/" only after two digits.
 */
export function maskDelimitedDate(raw: string): string | null {
  const iso = /^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/.exec(raw);
  if (iso) {
    const [, year, month, day] = iso;
    return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
  }

  const mdy = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{1,4})$/.exec(raw);
  if (mdy) {
    const [, month, day, year] = mdy;
    return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
  }

  return null;
}

/**
 * Reduces whatever is in the box to `mm/dd/yyyy` shape.
 *
 * A pasted date that carries its own separators (`9/2/2026`, `2026-08-14`) is
 * normalised by segment, so a single-digit month or day is zero-padded rather
 * than smeared across the wrong slots. Everything else — typing `08142026`,
 * editing in place, deleting a digit — is treated as a raw digit stream that the
 * separators are re-inserted into.
 */
export function applyDateMask(raw: string): string {
  const trimmed = (raw ?? '').trim();

  const delimited = maskDelimitedDate(trimmed);
  if (delimited !== null) return delimited;

  const digits = trimmed.replace(/\D/g, '').slice(0, 8);

  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/**
 * The segment the caret sits in, as a `[start, end]` selection range.
 *
 * A caret resting on a separator belongs to the segment before it, which is what
 * you want after typing the last digit of a segment.
 */
export function segmentRangeAt(text: string, caret: number): [number, number] {
  const clamped = Math.max(0, Math.min(caret, text.length));

  for (const [start, end] of SEGMENTS) {
    if (clamped <= end) return [start, Math.min(end, text.length)];
  }

  const last = SEGMENTS[SEGMENTS.length - 1];
  return [last[0], Math.min(last[1], text.length)];
}

/** Index of the segment the caret sits in: 0 month, 1 day, 2 year. */
export function segmentIndexAt(text: string, caret: number): number {
  const [start] = segmentRangeAt(text, caret);
  return SEGMENTS.findIndex(([segmentStart]) => segmentStart === start);
}

const SEGMENT_BOUNDS = [
  { min: 1, max: 12 }, // month
  { min: 1, max: 31 }, // day
  { min: 1, max: 9999 }, // year
];

/**
 * Steps the segment under the caret up or down, the way the native date input's
 * arrow keys did. Month and day wrap; the year just moves.
 *
 * Returns null when the field isn't a complete date yet, since there is nothing
 * meaningful to step.
 */
export function stepSegment(text: string, caret: number, delta: number): string | null {
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(text)) return null;

  const index = segmentIndexAt(text, caret);
  if (index < 0) return null;

  const parts = text.split('/');
  const { min, max } = SEGMENT_BOUNDS[index];
  const width = index === 2 ? 4 : 2;

  let next = Number(parts[index]) + delta;
  if (next > max) next = min;
  if (next < min) next = max;

  parts[index] = String(next).padStart(width, '0');

  const candidate = parts.join('/');

  // Stepping the month can land on a day that month doesn't have (31 → Feb).
  // Fall back to that month's last day rather than emitting an invalid date.
  if (displayToIso(candidate)) return candidate;

  const [month, , year] = candidate.split('/');
  const lastDay = new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate();
  parts[1] = String(lastDay).padStart(2, '0');

  return parts.join('/');
}
